import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ImagePlus, Loader2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabaseClient';
import { invokeErrorMessage } from '../lib/supabaseFunctionError';
import { mediaPreviewUrl, type MediaItem } from '../lib/mediaLibrary';
import MediaLibraryPicker from './MediaLibraryPicker';

type LocationRow = {
  id: string;
  location_name: string | null;
};

/** Composed GBP post returned by collect mode (multi-media campaign wizard). */
export interface GbpPostCollectDraft {
  connectionId: string;
  locationName: string | null;
  content: string;
  ctaType: string;
  ctaUrl: string;
  media: MediaItem | null;
}

export interface CreateGbpPostModalProps {
  open: boolean;
  onClose: () => void;
  /** Called after a successful publish or stage. */
  onSuccess?: () => void;
  /** Pre-select this connection. When set and found, location stays locked. */
  connectionId?: string | null;
  subtitle?: string;
  /** When set, open in "Stage for later" with this datetime-local value (YYYY-MM-DDTHH:mm). */
  initialScheduleLocal?: string | null;
  /**
   * Collect mode: don't publish/stage — validate and hand the composed post
   * back to the caller (multi-media campaign wizard). Hides the now/schedule picker.
   */
  collect?: {
    initialDraft?: GbpPostCollectDraft | null;
    onCollect: (draft: GbpPostCollectDraft) => void;
  } | null;
}

/**
 * Shared Google Business create-post modal (Media Manager, Control Centre, etc.).
 * Publish now or stage for later, with optional image + CTA.
 * Right-side sheet on desktop; full-height slide-up on mobile (matches Create Booking).
 */
export default function CreateGbpPostModal({
  open,
  onClose,
  onSuccess,
  connectionId: lockedConnectionId = null,
  subtitle = 'Publish now or stage for later',
  initialScheduleLocal = null,
  collect = null,
}: CreateGbpPostModalProps) {
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;

  const [isAnimating, setIsAnimating] = useState(false);
  const [locations, setLocations] = useState<LocationRow[]>([]);
  const [connectionId, setConnectionId] = useState<string>('');
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [postContent, setPostContent] = useState('');
  const [ctaType, setCtaType] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');
  const [postMode, setPostMode] = useState<'now' | 'schedule'>('now');
  const [scheduleLocal, setScheduleLocal] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [attachedMedia, setAttachedMedia] = useState<MediaItem | null>(null);
  const [showMediaPicker, setShowMediaPicker] = useState(false);

  useEffect(() => {
    if (open) setIsAnimating(true);
  }, [open]);

  useEffect(() => {
    if (!open || !businessId) return;
    let cancelled = false;
    (async () => {
      setLoadingLocations(true);
      try {
        const { data } = await supabase
          .from('google_business_connections')
          .select('id, location_name')
          .eq('business_id', businessId)
          .in('provider', ['ayrshare', 'google'])
          .eq('is_active', true)
          .order('created_at', { ascending: true });
        if (cancelled) return;
        const rows = (data || []) as LocationRow[];
        setLocations(rows);
        const preferred =
          (lockedConnectionId && rows.some((r) => r.id === lockedConnectionId)
            ? lockedConnectionId
            : null) ||
          rows[0]?.id ||
          '';
        setConnectionId(preferred);
      } finally {
        if (!cancelled) setLoadingLocations(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, businessId, lockedConnectionId]);

  useEffect(() => {
    if (!open) {
      setPostContent('');
      setCtaType('');
      setCtaUrl('');
      setPostMode('now');
      setScheduleLocal('');
      setPublishing(false);
      setAttachedMedia(null);
      setShowMediaPicker(false);
      return;
    }
    if (initialScheduleLocal) {
      setPostMode('schedule');
      setScheduleLocal(initialScheduleLocal);
    } else {
      setPostMode('now');
      setScheduleLocal('');
    }
    // Collect mode re-edit: restore the previously composed post.
    const draft = collect?.initialDraft;
    if (draft) {
      setPostContent(draft.content);
      setCtaType(draft.ctaType);
      setCtaUrl(draft.ctaUrl);
      setAttachedMedia(draft.media);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialScheduleLocal, collect?.initialDraft]);

  // Must stay above any early return — hooks order cannot change when `open` flips.
  // If the user switches Google location, drop a previously picked image so they
  // re-select from images unused for the new location. Only clear on an actual
  // change (not the initial load), so restored drafts keep their image.
  const prevConnectionIdRef = useRef('');
  useEffect(() => {
    if (prevConnectionIdRef.current && prevConnectionIdRef.current !== connectionId) {
      setAttachedMedia(null);
    }
    prevConnectionIdRef.current = connectionId;
  }, [connectionId]);

  const handleTransitionEnd = () => {
    if (!open) setIsAnimating(false);
  };

  const requestClose = () => {
    if (publishing) return;
    onClose();
  };

  if (!open && !isAnimating) return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
  const lockLocation = !!(
    lockedConnectionId && locations.some((r) => r.id === lockedConnectionId)
  );
  const multiLocation = locations.length > 1 && !lockLocation;
  const selectedLocation = locations.find((l) => l.id === connectionId);

  const handlePublish = async () => {
    if (!businessId || !connectionId) {
      toast.error('Select a Google Business location');
      return;
    }
    const content = postContent.trim();
    if (!content) {
      toast.error('Write a post first');
      return;
    }
    if (ctaType && !ctaUrl.trim()) {
      toast.error('Add a CTA URL or clear the CTA type');
      return;
    }

    if (collect) {
      collect.onCollect({
        connectionId,
        locationName: selectedLocation?.location_name || null,
        content,
        ctaType,
        ctaUrl: ctaUrl.trim(),
        media: attachedMedia,
      });
      return;
    }

    if (postMode === 'schedule') {
      if (!scheduleLocal) {
        toast.error('Pick a date and time to stage this post');
        return;
      }
      const when = new Date(scheduleLocal);
      if (Number.isNaN(when.getTime()) || when.getTime() <= Date.now()) {
        toast.error('Schedule time must be in the future');
        return;
      }
      setPublishing(true);
      try {
        const { data, error } = await supabase.functions.invoke('gbp-auto-post-action', {
          body: {
            action: 'stage_manual',
            businessId,
            connectionId,
            content,
            ctaType: ctaType || null,
            ctaUrl: ctaUrl.trim() || null,
            scheduledFor: when.toISOString(),
            mediaId: attachedMedia?.id || null,
          },
        });
        if (error || data?.error) {
          toast.error(
            data?.error || (await invokeErrorMessage(error, data)) || 'Failed to stage post',
          );
          return;
        }
        toast.success('Post staged for later');
        onSuccess?.();
        onClose();
      } catch (e) {
        console.error(e);
        toast.error('Failed to stage post');
      } finally {
        setPublishing(false);
      }
      return;
    }

    setPublishing(true);
    try {
      const { data, error } = await supabase.functions.invoke('ayrshare-gbp-create-post', {
        body: {
          businessId,
          connectionId,
          content,
          ctaType: ctaType || undefined,
          ctaUrl: ctaUrl.trim() || undefined,
          mediaUrl: attachedMedia?.url || undefined,
          mediaId: attachedMedia?.id || undefined,
        },
      });
      if (error || data?.error) {
        toast.error(
          data?.error || (await invokeErrorMessage(error, data)) || 'Failed to publish post',
        );
        return;
      }
      toast.success('Post published to Google Business');
      onSuccess?.();
      onClose();
    } catch (e) {
      console.error(e);
      toast.error('Failed to publish post');
    } finally {
      setPublishing(false);
    }
  };

  return createPortal(
    <>
      <div
        className={`fixed inset-0 transition-opacity duration-200 ${
          open ? 'bg-black/50' : 'bg-black/0'
        }`}
        style={{
          zIndex: 100200,
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) requestClose();
        }}
        onTransitionEnd={handleTransitionEnd}
      >
        <div
          role="dialog"
          aria-labelledby="create-gbp-post-title"
          aria-modal="true"
          className={`bg-white transform transition-all duration-200 ${
            isMobile
              ? `fixed left-0 right-0 w-full ${
                  open ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
                }`
              : `fixed inset-y-0 right-0 w-full md:w-[600px] shadow-2xl ${
                  open ? 'translate-x-0' : 'translate-x-full'
                }`
          }`}
          style={{
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
            zIndex: 100210,
            top: 'env(safe-area-inset-top, 0px)',
            bottom: 'env(safe-area-inset-bottom, 0px)',
            ...(isMobile && { left: 0, right: 0 }),
            ...(!isMobile && {
              height:
                'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
            }),
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="h-full flex flex-col overflow-hidden relative">
            <button
              type="button"
              onClick={requestClose}
              disabled={publishing}
              className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10 disabled:opacity-50"
              style={{ WebkitTapHighlightColor: 'transparent' }}
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center relative shrink-0">
              <h2
                id="create-gbp-post-title"
                className="text-2xl font-bold text-gray-900"
                style={{ letterSpacing: '-0.02em' }}
              >
                Create Google Business post
              </h2>
              <p className="mt-1 text-sm text-gray-500">{subtitle}</p>
            </div>

            <div
              className={`flex-1 overflow-y-auto bg-white ${isMobile ? 'p-4' : 'p-6'} space-y-5`}
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              {loadingLocations ? (
                <div className="flex items-center justify-center gap-2 py-16 text-sm text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading locations…
                </div>
              ) : locations.length === 0 ? (
                <p className="text-sm text-gray-500 py-16 text-center">
                  No Google Business locations connected.
                </p>
              ) : (
                <>
                  {multiLocation ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Location
                      </label>
                      <select
                        value={connectionId}
                        onChange={(e) => setConnectionId(e.target.value)}
                        className="w-full h-11 px-3 rounded-lg border border-gray-300 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      >
                        {locations.map((loc) => (
                          <option key={loc.id} value={loc.id}>
                            {loc.location_name || 'Google Business location'}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">
                      Posting to{' '}
                      <span className="font-medium text-gray-800">
                        {selectedLocation?.location_name ||
                          locations[0]?.location_name ||
                          'your Google Business location'}
                      </span>
                    </p>
                  )}

                  {!collect && (
                    <div className="grid grid-cols-2 gap-2">
                      {(
                        [
                          { id: 'now' as const, label: 'Publish now' },
                          { id: 'schedule' as const, label: 'Stage for later' },
                        ] as const
                      ).map((opt) => (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => setPostMode(opt.id)}
                          className={`rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${
                            postMode === opt.id
                              ? 'border-blue-600 bg-blue-50 text-blue-800'
                              : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}

                  {!collect && postMode === 'schedule' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Publish date & time
                      </label>
                      <input
                        type="datetime-local"
                        value={scheduleLocal}
                        onChange={(e) => setScheduleLocal(e.target.value)}
                        className="w-full h-11 px-3 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      />
                      <p className="text-xs text-gray-500 mt-1.5">
                        Uses your local timezone
                        {selectedBusiness?.timezone
                          ? ` · business TZ: ${selectedBusiness.timezone.replace(/_/g, ' ')}`
                          : ''}
                      </p>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Post content
                    </label>
                    <textarea
                      value={postContent}
                      onChange={(e) => setPostContent(e.target.value)}
                      rows={6}
                      maxLength={1500}
                      placeholder="Share an update, offer, or event with customers on Google…"
                      className="w-full text-sm rounded-lg border border-gray-300 px-3 py-2.5 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                    <p className="text-xs text-gray-400 mt-1.5">{postContent.length}/1500</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Image (optional)
                    </label>
                    {attachedMedia ? (
                      <div className="relative rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
                        <img
                          src={mediaPreviewUrl(attachedMedia.url)}
                          alt={attachedMedia.file_name || 'Attached'}
                          className="w-full h-44 object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => setAttachedMedia(null)}
                          disabled={publishing}
                          className="absolute top-2 right-2 h-8 w-8 flex items-center justify-center rounded-full bg-black/55 text-white hover:bg-black/70 disabled:opacity-50"
                          aria-label="Remove image"
                        >
                          <X className="h-4 w-4" />
                        </button>
                        {attachedMedia.file_name ? (
                          <p className="px-3 py-2 text-xs text-gray-600 truncate border-t border-gray-200 bg-white">
                            {attachedMedia.file_name}
                          </p>
                        ) : null}
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setShowMediaPicker(true)}
                        disabled={publishing || !businessId}
                        className="w-full flex items-center justify-center gap-2 rounded-xl border border-dashed border-gray-300 px-3 py-8 text-sm font-medium text-gray-600 hover:border-blue-400 hover:bg-blue-50/50 hover:text-blue-700 transition-colors disabled:opacity-50"
                      >
                        <ImagePlus className="h-4 w-4" />
                        Attach from Media library
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Call to action (optional)
                      </label>
                      <select
                        value={ctaType}
                        onChange={(e) => setCtaType(e.target.value)}
                        className="w-full h-11 px-3 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      >
                        <option value="">None</option>
                        <option value="LEARN_MORE">Learn more</option>
                        <option value="BOOK">Book</option>
                        <option value="ORDER">Order</option>
                        <option value="SHOP">Shop</option>
                        <option value="SIGN_UP">Sign up</option>
                        <option value="CALL">Call</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        CTA URL
                      </label>
                      <input
                        type="url"
                        value={ctaUrl}
                        onChange={(e) => setCtaUrl(e.target.value)}
                        disabled={!ctaType}
                        placeholder="https://"
                        className="w-full h-11 px-3 rounded-lg border border-gray-300 text-sm disabled:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>

            {locations.length > 0 && !loadingLocations ? (
              <div className="px-4 sm:px-6 py-4 border-t border-gray-200 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end bg-white shrink-0">
                <button
                  type="button"
                  onClick={requestClose}
                  disabled={publishing}
                  className="px-4 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void handlePublish()}
                  disabled={publishing || !postContent.trim() || !connectionId}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {publishing && <Loader2 className="h-4 w-4 animate-spin" />}
                  {collect ? 'Continue' : postMode === 'schedule' ? 'Stage post' : 'Publish to Google'}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {businessId ? (
        <MediaLibraryPicker
          isOpen={showMediaPicker}
          businessId={businessId}
          multiple={false}
          zIndex={100220}
          excludeUsedFor={
            connectionId
              ? { platform: 'google', connectionId }
              : null
          }
          initialSelectedUrls={attachedMedia ? [attachedMedia.url] : []}
          onClose={() => setShowMediaPicker(false)}
          onDone={(selected) => {
            setAttachedMedia(selected[0] || null);
            setShowMediaPicker(false);
          }}
        />
      ) : null}
    </>,
    document.body,
  );
}
